/**
 * Content (blog) attribution URL + campaign encoding — the blog analogue of
 * `src/lib/social/utm.ts`.
 *
 * A blog post is attributed by its `blog_posts.slug`. The first-touch cookie
 * (`ld_attr`, see `posthog-provider`) and the signup/apply hooks carry it as a
 * `utm_campaign` value prefixed `bp_` (blog post), so the shared first-touch
 * plumbing (`utm_source=content`, `utm_campaign=bp_<slug>`) attributes a later
 * signup/apply back to the post the reader first landed on — exactly mirroring
 * the social `sp_<postId>` convention, on a separate namespace so the two never
 * collide.
 *
 * The `buildContentUrl` helper tags OWNED-channel links (newsletter, RSS) so a
 * reader arriving from them is credited with the right `medium`. Organic search
 * arrivals need no tag: the client beacon records the click from the landing
 * path itself.
 */

export const CONTENT_CAMPAIGN_PREFIX = "bp_";

/** Encode a blog post slug as a first-touch `utm_campaign` value. */
export function contentCampaign(slug: string): string {
	return `${CONTENT_CAMPAIGN_PREFIX}${slug}`;
}

/**
 * Decode a `utm_campaign` back to the originating blog post slug. Returns null
 * for non-content campaigns (social `sp_`/`lt_`, empty, etc.), so call sites can
 * cheaply tell "did a blog post drive this?" apart from a social/referral touch.
 */
export function slugFromContentCampaign(
	utmCampaign: string | null | undefined,
): string | null {
	const v = utmCampaign?.trim();
	if (!v?.startsWith(CONTENT_CAMPAIGN_PREFIX)) return null;
	const slug = v.slice(CONTENT_CAMPAIGN_PREFIX.length);
	return slug.length > 0 ? slug : null;
}

/** True when a first-touch was a trackable content (blog) arrival. */
export function isContentCampaign(input: {
	source?: string | null;
	campaign?: string | null;
}): boolean {
	return (
		input.source === "content" &&
		slugFromContentCampaign(input.campaign) !== null
	);
}

export function appBaseUrl(): string {
	return (
		process.env.NEXT_PUBLIC_APP_URL?.replace(/\/+$/, "") ||
		"https://lokerdollar.com"
	);
}

/**
 * Build a UTM-tagged link to a blog post for an OWNED distribution channel
 * (newsletter, RSS). `utm_source=content`, `utm_campaign=bp_<slug>`,
 * `utm_medium=<channel>`. Landing on the post fires the client beacon, which
 * reads the medium from the URL. Organic/internal links do not need this.
 */
export function buildContentUrl(input: {
	slug: string;
	locale: string;
	medium: "newsletter" | "rss" | "social";
}): string {
	const u = new URL(`${appBaseUrl()}/${input.locale}/blog/${input.slug}`);
	u.searchParams.set("utm_source", "content");
	u.searchParams.set("utm_medium", input.medium);
	u.searchParams.set("utm_campaign", contentCampaign(input.slug));
	return u.toString();
}
